import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  signal,
  inject,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import {
  Stay,
  StayStatus,
  TimelineStayManagementDrawerFormValue,
} from '@wilson/interfaces';

import {
  SetDemarcatorRange,
  ShiftTimelineDataState,
  TimelineRemoveStay,
  TimelineStaysSuggestionService,
  TimelineUpsertNewStay,
  TimelineSetPreviousUserInStay,
} from '@wilson/shift-timeline/state';
import { firstValueFrom } from 'rxjs';
import { TimelineStayGateway } from '@wilson/api/gateway';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  StaysDrawerStateService,
  EditStaysDrawerViewComponent,
  StaysDrawerHelperService,
  StaysAttachmentsHelperService,
  StayPDFType,
} from '@wilson/non-domain-specific/stays-form-and-drawer';
import { SectorState } from '@wilson/sectors';
import { Store } from '@ngxs/store';
import { captureException } from '@sentry/angular';
import { GroupItemsSelectionHelperService } from '@wilson/shift-timeline/assignments-gantt-chart';
import { DrawerGroupingActionsService } from '@wilson/non-domain-specific/groupings';

@Component({
  selector: 'wilson-timeline-stays-edit-drawer',
  imports: [
    CommonModule,
    TranslateModule,
    NzDrawerModule,
    NzButtonModule,
    EditStaysDrawerViewComponent,
  ],
  providers: [StaysAttachmentsHelperService, DrawerGroupingActionsService],
  templateUrl: './timeline-stays-edit-drawer.component.html',
  styleUrl: './timeline-stays-edit-drawer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimelineStaysEditDrawerComponent implements OnDestroy {
  private store = inject(Store);
  protected timelineStayGateway = inject(TimelineStayGateway);
  protected timelineStaysSuggestionService = inject(
    TimelineStaysSuggestionService,
  );
  private readonly translate = inject(TranslateService);
  private readonly nzMessageService = inject(NzMessageService);
  private readonly staysDrawerStateService = inject(StaysDrawerStateService);
  protected readonly staysDrawerHelperService = inject(
    StaysDrawerHelperService,
  );
  protected readonly staysAttachmentsHelperService = inject(
    StaysAttachmentsHelperService,
  );
  private readonly groupItemsSelectionHelperService = inject(
    GroupItemsSelectionHelperService,
  );
  private readonly drawerGroupingActions = inject(DrawerGroupingActionsService);

  protected readonly isFormInvalidSignal = signal(false);
  protected readonly isFormPristineSignal = signal(true);
  protected readonly isOperationOnGoingSignal = signal(false);
  protected isEditStaysDrawerVisible$ =
    this.staysDrawerStateService.isEditStaysDrawerVisible$;

  protected stayToEdit$ = this.staysDrawerStateService.stayToEdit$;
  protected sector$ = this.store.select(SectorState.firstSector);

  constructor() {
    this.drawerGroupingActions.configure({
      drawerItemType: 'stay',
      deferUntilFlush: true,
    });
  }

  handleFailedToFillForm(): void {
    this.nzMessageService.error(
      this.translate.instant('general.something_went_wrong'),
    );
    this.staysDrawerStateService.resetEditStayData();
    this.groupItemsSelectionHelperService.clearSelectedItems();
    this.store.dispatch([
      new SetDemarcatorRange([
        {
          left: null,
          right: null,
        },
      ]),
    ]);
  }

  async deleteStay(): Promise<void> {
    try {
      this.isOperationOnGoingSignal.set(true);
      const stay = await firstValueFrom(
        this.staysDrawerStateService.stayToEdit$,
      );
      if (stay) {
        await this.timelineStayGateway.deleteStay(stay.id);
        this.store.dispatch([
          new TimelineRemoveStay({ stayId: stay.id, userId: stay.userId }),
        ]);

        await this.timelineStaysSuggestionService.refreshStaysDataForSpecifiedUsers(
          [stay.userId],
        );
        this.nzMessageService.success(
          this.translate.instant('general.delete.successful'),
        );
        this.isOperationOnGoingSignal.set(false);
        this.drawerGroupingActions.discardPendingActions();
        this.staysDrawerStateService.setEditStaysDrawerVisibility(false);
        this.groupItemsSelectionHelperService.clearSelectedItems();
      }
    } catch (error) {
      this.nzMessageService.error(
        this.translate.instant('general.something_went_wrong'),
      );

      captureException(error);
      this.isOperationOnGoingSignal.set(false);
    }
  }

  closeDrawer(): void {
    this.drawerGroupingActions.discardPendingActions();
    this.staysDrawerStateService.setEditStaysDrawerVisibility(false);
    this.staysDrawerStateService.resetEditStayData();
    this.store.dispatch([new SetDemarcatorRange([])]);
    this.groupItemsSelectionHelperService.clearSelectedItems();
  }

  async confirm(
    staysPartial: Partial<TimelineStayManagementDrawerFormValue>,
  ): Promise<Stay | null> {
    const modalRef = this.staysAttachmentsHelperService.openLoaderDialog();
    this.isOperationOnGoingSignal.set(true);
    try {
      const stay = await firstValueFrom(
        this.staysDrawerStateService.stayToEdit$,
      );
      if (stay) {
        this.store.dispatch(
          new TimelineSetPreviousUserInStay({
            userId: stay.userId,
          }),
        );
        const staysObject =
          this.staysDrawerHelperService.prepareStaysObject(staysPartial);

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { attachments, ...stayWithoutAttachments } = stay;

        const result = await firstValueFrom(
          this.timelineStayGateway.updateStay({
            ...stayWithoutAttachments,
            ...staysObject,
          }),
        );

        await this.staysAttachmentsHelperService.handleFileUpload(stay.id);

        if (result) {
          await this.drawerGroupingActions.flushPendingActions();
          const stays = await firstValueFrom(
            this.timelineStayGateway.getStays({
              endDatetime: result.endDatetime,
              startDatetime: result.startDatetime,
              statuses: [result.status],
              userIds: [result.userId],
            }),
          );
          const stayToUpdate = stays.find((stay) => stay.id === result.id);

          const stayToModify = stayToUpdate || result;
          const previousUserId = await firstValueFrom(
            this.store.select(ShiftTimelineDataState.previousUserIdInStay),
          );

          if (
            stayToModify.status === StayStatus.Cancelled ||
            stayToModify.status === StayStatus.ShortTermCancelled
          ) {
            this.store.dispatch(
              new TimelineRemoveStay({
                stayId: stayToModify.id,
                userId: stayToModify.userId,
              }),
            );
          } else {
            this.store.dispatch(new TimelineUpsertNewStay(stayToModify));
          }

          if (previousUserId !== result.userId) {
            this.store.dispatch(
              new TimelineRemoveStay({
                stayId: stayToModify.id,
                userId: previousUserId,
              }),
            );

            await this.timelineStaysSuggestionService.refreshStaysDataForSpecifiedUsers(
              [previousUserId],
            );
          }

          await this.timelineStaysSuggestionService.refreshStaysDataForSpecifiedUsers(
            [result.userId],
          );

          this.nzMessageService.success(
            this.translate.instant('general.saved_successfully'),
          );

          this.staysDrawerStateService.setEditStaysDrawerVisibility(false);
          this.groupItemsSelectionHelperService.clearSelectedItems();
          this.isOperationOnGoingSignal.set(false);
          modalRef.close();
        }
        return result;
      }
    } catch (error) {
      this.isOperationOnGoingSignal.set(false);
      modalRef.close();
      console.error('An error occurred while editing a stay:', error);
      this.nzMessageService.error(
        this.translate.instant('general.something_went_wrong'),
      );
      throw error;
    }
    return null;
  }

  protected async confirmAndCreateReimbursement(
    formValue: Partial<TimelineStayManagementDrawerFormValue>,
  ): Promise<void> {
    const stay = await this.confirm(formValue);
    if (stay) {
      try {
        const updatedStay = await firstValueFrom(
          this.timelineStayGateway.updateStay({
            id: stay.id,
            status: StayStatus.Requested,
          }),
        );
        await firstValueFrom(
          this.staysDrawerHelperService.createHotelReimbursement(
            stay,
            StayPDFType.Reimbursement,
          ),
        );
        this.store.dispatch(new TimelineUpsertNewStay(updatedStay));
        await this.timelineStaysSuggestionService.refreshStaysDataForSpecifiedUsers(
          [stay.userId],
        );
      } catch {
        this.nzMessageService.error(
          this.translate.instant('general.something_went_wrong'),
        );
      }
    }
  }

  protected async cancelStayAndCreatePdf(
    formValue: Partial<TimelineStayManagementDrawerFormValue>,
  ): Promise<void> {
    const stay = await this.confirm(formValue);
    if (stay) {
      await firstValueFrom(
        this.staysDrawerHelperService.createHotelReimbursement(
          stay,
          StayPDFType.Cancellation,
        ),
      );
    }
  }

  ngOnDestroy(): void {
    this.drawerGroupingActions.discardPendingActions();
    this.staysDrawerStateService.resetEditStayData();
    this.groupItemsSelectionHelperService.clearSelectedItems();
  }
}
